import React, { useState, useEffect } from "react";
import {
  Key,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  Activity,
  X,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { apiManagementService } from "../services";
import toast from "react-hot-toast";

const ApiManagementPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const keyForm = useForm();

  const permissions = [
    { value: "products:read", label: "Read Products" },
    { value: "products:purchase", label: "Purchase Products" },
    { value: "wallet:read", label: "Read Wallet" },
    { value: "wallet:write", label: "Write Wallet" },
    { value: "transactions:read", label: "Read Transactions" },
  ];

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const response = await apiManagementService.getAllApiKeys();
      if (response.status === "success") {
        setApiKeys(response.data.api_keys);
      }
    } catch (error) {
      console.error("Error loading API keys:", error);
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (data) => {
    try {
      let scopes = data.scopes;
      if (typeof scopes === "string") {
        scopes = [scopes];
      } else if (Array.isArray(scopes)) {
        scopes = scopes.filter((scope) => scope && scope.trim() !== "");
      }

      const response = await apiManagementService.createApiKeyDirect({
        key_name: data.key_name,
        key_description: data.key_description,
        scopes: scopes,
      });

      if (response.status === "success") {
        const newKey = response.data.api_key;
        setNewlyCreatedKey(newKey);
        setShowCreateKey(false);
        keyForm.reset();
        loadApiKeys();
        toast.success("API key created!");
      }
    } catch (error) {
      console.error("Error creating API key:", error);
      toast.error("Failed to create API key");
    }
  };

  const regenerateApiKey = async (keyId) => {
    setRegeneratingKey(keyId);
    try {
      const response = await apiManagementService.regenerateApiKey(keyId);
      if (response.status === "success") {
        const newKey = response.data.api_key;
        setNewlyCreatedKey(newKey);
        loadApiKeys();
        toast.success("Key regenerated!");
      }
    } catch (error) {
      console.error("Error regenerating API key:", error);
      toast.error("Failed to regenerate key");
    } finally {
      setRegeneratingKey(null);
    }
  };

  const revokeApiKey = async (keyId) => {
    try {
      const response = await apiManagementService.revokeApiKeyDirect(keyId);
      if (response.status === "success") {
        setApiKeys((prev) => prev.filter((key) => key.key_id !== keyId));
        toast.success("Key revoked!");
      }
    } catch (error) {
      console.error("Error revoking API key:", error);
      toast.error("Failed to revoke key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const handleConfirmAction = (action) => {
    action();
    setConfirmAction(null);
  };

  const toggleAllPermissions = () => {
    const currentScopes = keyForm.watch("scopes") || [];
    if (currentScopes.length === permissions.length) {
      keyForm.setValue("scopes", []);
    } else {
      keyForm.setValue(
        "scopes",
        permissions.map((p) => p.value),
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-600 text-sm">Manage your API access keys</p>
        </div>
        <button
          onClick={() => setShowCreateKey(true)}
          className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Key</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Fixed Stats Cards for Mobile */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200">
          <div className="text-center sm:flex sm:items-center sm:justify-between">
            <div className="sm:text-left">
              <p className="text-xs sm:text-sm text-gray-500 font-medium">
                Total
              </p>
              <p className="text-lg sm:text-xl font-bold text-gray-900">
                {apiKeys.length}
              </p>
            </div>
            <div className="hidden sm:block p-2 bg-blue-100 rounded-lg">
              <Key className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200">
          <div className="text-center sm:flex sm:items-center sm:justify-between">
            <div className="sm:text-left">
              <p className="text-xs sm:text-sm text-gray-500 font-medium">
                Active
              </p>
              <p className="text-lg sm:text-xl font-bold text-green-600">
                {apiKeys.filter((key) => key.status === "active").length}
              </p>
            </div>
            <div className="hidden sm:block p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200">
          <div className="text-center sm:flex sm:items-center sm:justify-between">
            <div className="sm:text-left">
              <p className="text-xs sm:text-sm text-gray-500 font-medium">
                Calls
              </p>
              <p className="text-lg sm:text-xl font-bold text-gray-900">0</p>
            </div>
            <div className="hidden sm:block p-2 bg-purple-100 rounded-lg">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Keys List */}
      <div className="bg-white rounded-xl border border-gray-200">
        {apiKeys.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              No API keys yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first key to start using our API
            </p>
            <button
              onClick={() => setShowCreateKey(true)}
              className="bg-yellow-400 text-black px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors"
            >
              Create Your First Key
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {apiKeys.map((key) => (
              <div
                key={key.key_id}
                className="p-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-0">
                      <h3 className="font-semibold text-gray-900">
                        {key.key_name}
                      </h3>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {key.status}
                      </span>
                    </div>
                    {key.key_description && (
                      <p className="text-gray-400 mb-3 text-sm">
                        {key.key_description}
                      </p>
                    )}
                    <p className="text-xs text-gray-600">{key.client_name}</p>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() =>
                        setConfirmAction({
                          title: "Regenerate API Key?",
                          message:
                            "This will create a new key and invalidate the old one.",
                          action: () => regenerateApiKey(key.key_id),
                          type: "warning",
                        })
                      }
                      disabled={regeneratingKey === key.key_id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Regenerate key"
                    >
                      <RefreshCw
                        className={`w-4 h-4 ${
                          regeneratingKey === key.key_id ? "animate-spin" : ""
                        }`}
                      />
                    </button>
                    <button
                      onClick={() =>
                        setConfirmAction({
                          title: "Revoke API Key?",
                          message: "This action cannot be undone.",
                          action: () => revokeApiKey(key.key_id),
                          type: "danger",
                        })
                      }
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Revoke key"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Key Preview */}
                <div className="bg-gray-900 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <code className="text-green-400 font-mono text-sm">
                      {key.api_key_preview}...
                    </code>
                  </div>
                </div>

                {/* Scopes */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {key.scopes.map((scope) => (
                    <span
                      key={scope}
                      className="px-2 py-1 bg-slate-200 text-slate-500 text-xs rounded-lg font-light"
                    >
                      {scope.replace(":", " ")}
                    </span>
                  ))}
                </div>

                <div className="flex justify-between text-sm text-gray-500">
                  <span>Unlimited requests/hour</span>
                  <span>
                    Created {new Date(key.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal - Reverted permissions layout */}
      {showCreateKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Create API Key
                </h2>
                <button
                  onClick={() => {
                    setShowCreateKey(false);
                    keyForm.reset();
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form
              onSubmit={keyForm.handleSubmit(createApiKey)}
              className="p-5 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Name *
                </label>
                <input
                  {...keyForm.register("key_name", {
                    required: "Name is required",
                  })}
                  style={{ fontSize: "16px" }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                  placeholder="Production Key"
                />
                {keyForm.formState.errors.key_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {keyForm.formState.errors.key_name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  {...keyForm.register("key_description")}
                  style={{ fontSize: "16px" }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                  placeholder="What this key will be used for"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions *
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllPermissions}
                    className="text-sm text-yellow-600 hover:text-yellow-700 font-medium"
                  >
                    Select All
                  </button>
                </div>
                {/* Back to vertical layout */}
                <div className="space-y-3">
                  {permissions.map((scope) => (
                    <label
                      key={scope.value}
                      className="flex items-center space-x-3"
                    >
                      <input
                        type="checkbox"
                        value={scope.value}
                        {...keyForm.register("scopes", {
                          required: "Select at least one permission",
                        })}
                        className="rounded border-gray-300 text-yellow-400 focus:ring-yellow-400"
                      />
                      <span className="text-sm text-gray-700">
                        {scope.label}
                      </span>
                    </label>
                  ))}
                </div>
                {keyForm.formState.errors.scopes && (
                  <p className="text-red-500 text-sm mt-1">
                    {keyForm.formState.errors.scopes.message}
                  </p>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-700">
                    Your API key will only be shown once after creation.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateKey(false);
                    keyForm.reset();
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 font-medium transition-colors"
                >
                  Create Key
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm">
            <div className="p-5 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  confirmAction.type === "danger"
                    ? "bg-red-100"
                    : "bg-yellow-100"
                }`}
              >
                <AlertTriangle
                  className={`w-6 h-6 ${
                    confirmAction.type === "danger"
                      ? "text-red-600"
                      : "text-yellow-600"
                  }`}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmAction.title}
              </h3>
              <p className="text-gray-600 mb-5">{confirmAction.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirmAction(confirmAction.action)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    confirmAction.type === "danger"
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-yellow-400 text-black hover:bg-yellow-500"
                  }`}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {newlyCreatedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-5 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Key Created!
              </h2>
              <p className="text-gray-600 mb-5">
                Copy your API key now. You won't see it again.
              </p>

              <div className="bg-gray-900 rounded-lg p-4 mb-5">
                <div className="flex items-center justify-between">
                  <code className="text-green-400 font-mono text-sm break-all flex-1 mr-3">
                    {newlyCreatedKey.api_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey.api_key)}
                    className="bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <button
                onClick={() => setNewlyCreatedKey(null)}
                className="w-full bg-yellow-400 text-black py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors"
              >
                I've Saved My Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiManagementPage;
